/* eslint-disable no-unused-vars */
// libraries
import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";

// styles
import styles from "./styles.module.scss";

export enum InputType {
  DEFAULT = "DEFAULT",
  NUMBER = "NUMBER",
  PASSWORD = "PASSWORD",
}

interface Props extends React.HTMLProps<HTMLInputElement> {
  label?: string;
  defaultValue?: string;
  maxCharacters?: number;
  // eslint-disable-next-line no-unused-vars
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: InputType;
  required?: boolean;
}

const TextInput: React.FC<Props> = ({
  label = "",
  defaultValue = "",
  maxCharacters = 100,
  handleChange,
  required,
  type = InputType.DEFAULT,
  ...props
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState<string | number>("");
  const [error, setError] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setError(false);
      setValue(e.target.value);
      handleChange(e);
    },
    [handleChange]
  );

  useEffect(() => {
    if (required && !value && focused) {
      setError(true);
    }
    if (focused && defaultValue) {
      setValue(defaultValue);
      setError(false);
    }
  }, [defaultValue, focused, required, value]);

  const validateNumber = (
    value: React.KeyboardEvent<HTMLTextAreaElement>["key"]
  ) => {
    if (/[0-9]/.test(value)) return true;
    return false;
  };

  const errorClass = error ? styles.error : "";

  return (
    <div className={classNames(styles.container, errorClass)}>
      <div
        className={classNames(
          styles["header"],
          focused || inputRef?.current?.value || value
            ? styles["header_active"]
            : styles["header_inactive"]
        )}>
        <p className={classNames("inputHint", "g400")}>{label}</p>
      </div>
      <input
        ref={inputRef}
        className={classNames(
          classNames(styles["text_area"], "input2"),
          type === InputType.NUMBER
            ? styles["number"]
            : type === InputType.PASSWORD
            ? styles["password"]
            : ""
        )}
        aria-label="maximum height"
        defaultValue={defaultValue}
        placeholder={focused ? "" : label}
        onChange={onChange}
        disabled={String(value).length >= maxCharacters}
        style={{
          minHeight: "16px",
          width: "100%",
        }}
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={() => {
          setFocused(false);
        }}
        onKeyPress={(event: { key: string; preventDefault: () => void }) => {
          if (!validateNumber(event?.key) && type === InputType.NUMBER)
            event.preventDefault();
        }}
        {...props}
      />
    </div>
  );
};

export default React.memo(TextInput);
